import type { ReactNode } from "react";
import { usePunditUserContext } from "@circle-react/contexts";
import type { AccessGroupEntryPoint } from "@circle-react/helpers/accessGroups/accessGroupHelpers";
import { useListAccessGroupSourcesQuery } from "@circle-react/hooks/accessGroups/useListAccessGroupSourcesQuery";
import { useAccessGroups } from "@circle-react/hooks/accessRegistry/useAccessGroups";
import { Loader } from "@circle-react-shared/uikit/Loader";
import { Form } from "@circle-react-uikit/Form";
import { useAccessGroupConfirmationModal } from "./AccessGroupConfirmationModal";
import { AccessGroupContent } from "./AccessGroupContent";
import { AccessGroupHeader } from "./AccessGroupHeader";
import { AccessGroupSourcesDataProvider } from "./DataStoreContext/AccessGroupSourcesDataContext";
import { useUpdateAccessGroupsSources } from "./hooks/useUpdateAccessGroupsSources";
import type { ConfirmationDetailsProps } from "./types";

interface AccessGroupsProps {
  addConfirmationDetails?: ConfirmationDetailsProps;
  areFormItemsEmbedded?: boolean;
  canRemoveAllAccessGroups?: boolean;
  isAccessGroupsTabEnabled?: boolean;
  isSyncing?: boolean;
  recordId?: number;
  recordType: AccessGroupEntryPoint;
  spaceAccessNode?: ReactNode;
  syncingTooltip?: string;
}

export const tabs = Object.freeze({
  access_groups: "access_groups",
  space_access: "space_access",
});

export const AccessGroups = ({
  recordType,
  recordId,
  spaceAccessNode,
  isAccessGroupsTabEnabled = true,
  canRemoveAllAccessGroups = true,
  areFormItemsEmbedded = false,
  isSyncing = false,
  syncingTooltip = "",
  addConfirmationDetails,
}: AccessGroupsProps) => {
  const { currentCommunitySettings } = usePunditUserContext();
  const { accessGroupSources, isLoadingAccessGroupSources } =
    useListAccessGroupSourcesQuery({
      recordType: recordType,
      recordId: recordId,
    });

  const { data: accessGroups, isLoading: isLoadingAccessGroups } =
    useAccessGroups();

  const { udpateAccessGroupSourcesHandler, isUpdatingAccessGroupSources } =
    useUpdateAccessGroupsSources();

  const accessConfirmationModal = useAccessGroupConfirmationModal();

  if (isLoadingAccessGroupSources || isLoadingAccessGroups) {
    return <Loader />;
  }

  const getDefaults = () => ({
    access_groups: [],
    recordType: recordType,
    recordId: recordId,
    access_groups_enabled: isAccessGroupsTabEnabled,
  });

  const updateRecords = (data: any) => {
    udpateAccessGroupSourcesHandler({
      access_groups: data.access_groups.map((accessGroup: any) => ({
        id: accessGroup.id,
        status: accessGroup.status,
      })),
      record_type: data.recordType,
      record_id: data.recordId,
    });
  };

  const shouldShowAccessOptions = Boolean(
    spaceAccessNode &&
      !currentCommunitySettings?.block_access_groups_for_trials_enabled,
  );

  const formContent = (
    <AccessGroupContent
      isUpdatingAccessGroupSources={isUpdatingAccessGroupSources}
      spaceAccessNode={spaceAccessNode}
      shouldShowAccessOptions={shouldShowAccessOptions}
    />
  );

  const handleSubmit = (data: any) => {
    // eslint-disable-next-line sonarjs/no-selector-parameter -- Disabled for plugin version update. Fix when possible
    if (spaceAccessNode && !isAccessGroupsTabEnabled) {
      void accessConfirmationModal.show({
        i18nRoot:
          "settings.access_groups.admin.entry_points.association.actions.change_to_access_groups_confirmation",
        onConfirm: () => {
          updateRecords(data);
        },
      });
    } else {
      updateRecords(data);
    }
  };

  return (
    <AccessGroupSourcesDataProvider
      accessGroups={accessGroups ?? []}
      originalAccessGroupSources={accessGroupSources ?? []}
      recordType={recordType}
      recordId={recordId}
      areFormItemsEmbedded={areFormItemsEmbedded}
      canRemoveAllAccessGroups={canRemoveAllAccessGroups}
      isSyncing={isSyncing}
      syncingTooltip={syncingTooltip}
      addConfirmationDetails={addConfirmationDetails}
    >
      <AccessGroupHeader
        areFormItemsEmbedded={areFormItemsEmbedded}
        recordType={recordType}
      />

      {areFormItemsEmbedded ? (
        formContent
      ) : (
        <>
          <Form
            defaultValues={getDefaults()}
            onSubmit={data => handleSubmit(data)}
          >
            {formContent}
          </Form>
          <div id="access-groups-form" />
        </>
      )}
    </AccessGroupSourcesDataProvider>
  );
};
